import React, { useState, useEffect } from 'react';
import { Card, Button, Form, Alert, Spinner, Row, Col, Badge } from 'react-bootstrap';
import api from './api';

// Tanımlı tema presetleri
const DARK_THEMES = [
  { key: 'bento-dark', name: 'Bento Karanlık', colors: ['#0F1311', '#3FA45B', '#C9F27B'] },
  { key: 'dark-clinic', name: 'Klinik Yeşil', colors: ['#0f1714', '#6de039', '#ff83c4'] },
  { key: 'dark-ocean', name: 'Gece Mavisi', colors: ['#0b1120', '#60a5fa', '#c084fc'] },
  { key: 'dark-charcoal', name: 'Sıcak Kömür', colors: ['#1a1a1a', '#f59e0b', '#f472b6'] },
  { key: 'dark-purple', name: 'Mor Gece', colors: ['#13111c', '#a78bfa', '#fb923c'] },
  { key: 'dark-rose', name: 'Rose', colors: ['#18111a', '#fb7185', '#38bdf8'] },
];

const LIGHT_THEMES = [
  { key: 'bento-light', name: 'Bento Aydınlık', colors: ['#F4F4EF', '#1F3D2B', '#C9F27B'] },
  { key: 'light-clinic', name: 'Klinik Beyaz', colors: ['#f7f9fc', '#266d00', '#a03573'] },
  { key: 'light-ocean', name: 'Okyanus', colors: ['#f0f5ff', '#2563eb', '#7c3aed'] },
  { key: 'light-sand', name: 'Sıcak Kum', colors: ['#faf7f2', '#b45309', '#059669'] },
  { key: 'light-lavender', name: 'Lavanta', colors: ['#f5f3ff', '#7c3aed', '#ec4899'] },
  { key: 'light-mint', name: 'Nane Yeşili', colors: ['#f0fdf4', '#059669', '#0284c7'] },
];

function Profil({ theme, themePreset, onThemeChange }) {
  const [profil, setProfil] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({ eposta: '', telefon: '' });
  const [profilMsg, setProfilMsg] = useState({ text: '', type: '' });

  // Şifre değiştirme state
  const [mevcutSifre, setMevcutSifre] = useState('');
  const [yeniSifre, setYeniSifre] = useState('');
  const [yeniSifreTekrar, setYeniSifreTekrar] = useState('');
  const [sifreMsg, setSifreMsg] = useState({ text: '', type: '' });
  const [sifreLoading, setSifreLoading] = useState(false);

  // Logo state
  const [logoMsg, setLogoMsg] = useState({ text: '', type: '' });
  const [logoLoading, setLogoLoading] = useState(false);

  useEffect(() => {
    api.get('/diyetisyen/profil')
      .then(res => {
        setProfil(res.data);
        setEditData({ eposta: res.data.eposta || '', telefon: res.data.telefon || '' });
      })
      .catch(() => setProfil(null))
      .finally(() => setLoading(false));
  }, []);

  const handleProfilUpdate = async (e) => {
    e.preventDefault();
    setProfilMsg({ text: '', type: '' });
    try {
      await api.put('/diyetisyen/profil', editData);
      setProfilMsg({ text: 'Profil başarıyla güncellendi!', type: 'success' });
      setProfil(prev => ({ ...prev, ...editData }));
      setEditMode(false);
    } catch (err) {
      setProfilMsg({ text: err.response?.data?.mesaj || 'Güncelleme başarısız.', type: 'danger' });
    }
  };

  const handleSifreDegistir = async (e) => {
    e.preventDefault();
    setSifreMsg({ text: '', type: '' });
    if (yeniSifre.length < 8) {
      setSifreMsg({ text: 'Şifre en az 8 karakter olmalıdır.', type: 'danger' });
      return;
    }
    if (yeniSifre !== yeniSifreTekrar) {
      setSifreMsg({ text: 'Şifreler eşleşmiyor.', type: 'danger' });
      return;
    }
    setSifreLoading(true);
    try {
      await api.post('/sifre-degistir', { mevcut_sifre: mevcutSifre, yeni_sifre: yeniSifre });
      setSifreMsg({ text: 'Şifreniz başarıyla değiştirildi!', type: 'success' });
      setMevcutSifre(''); setYeniSifre(''); setYeniSifreTekrar('');
    } catch (err) {
      setSifreMsg({ text: err.response?.data?.mesaj || 'Şifre değiştirilemedi.', type: 'danger' });
    } finally {
      setSifreLoading(false);
    }
  };

  // Seçilen görseli en fazla 400px genişliğe küçültüp data URL'e çevirir (500 KB limiti için)
  const gorseliKucult = (dosya) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const maxGenislik = 400;
        const oran = Math.min(1, maxGenislik / img.width);
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(img.width * oran);
        canvas.height = Math.round(img.height * oran);
        canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL('image/png'));
      };
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(dosya);
  });

  const handleLogoSec = async (e) => {
    const dosya = e.target.files?.[0];
    e.target.value = '';
    if (!dosya) return;
    if (!dosya.type.startsWith('image/')) {
      setLogoMsg({ text: 'Lütfen bir görsel dosyası seçin (PNG, JPEG, WebP).', type: 'danger' });
      return;
    }
    setLogoLoading(true);
    setLogoMsg({ text: '', type: '' });
    try {
      const dataUrl = await gorseliKucult(dosya);
      await api.put('/diyetisyen/logo', { logo_data: dataUrl });
      setProfil(prev => ({ ...prev, logo_data: dataUrl }));
      setLogoMsg({ text: 'Logonuz kaydedildi. Artık panelde ve beslenme planı çıktılarında görünecek.', type: 'success' });
    } catch (err) {
      setLogoMsg({ text: err.response?.data?.mesaj || 'Logo yüklenemedi.', type: 'danger' });
    } finally {
      setLogoLoading(false);
    }
  };

  const handleLogoKaldir = async () => {
    if (!window.confirm('Logonuzu kaldırmak istediğinizden emin misiniz?')) return;
    try {
      await api.delete('/diyetisyen/logo');
      setProfil(prev => ({ ...prev, logo_data: null }));
      setLogoMsg({ text: 'Logo kaldırıldı.', type: 'success' });
    } catch (err) {
      setLogoMsg({ text: 'Logo kaldırılamadı.', type: 'danger' });
    }
  };

  const inputStyle = {
    backgroundColor: 'transparent',
    color: 'var(--text-main)',
    border: '1px solid var(--border-color)',
  };

  if (loading) return <div className="d-flex justify-content-center py-5"><Spinner animation="border" style={{ color: 'var(--accent-primary)' }} /></div>;

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto' }}>
      {/* PROFIL BAŞLIĞI */}
      <div className="d-flex align-items-center gap-4 mb-5">
        <div className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
          style={{ width: '90px', height: '90px', background: `linear-gradient(135deg, var(--accent-primary), var(--accent-tertiary))`, color: 'white', fontSize: '2.2rem', fontWeight: 'bold' }}>
          <span className="material-symbols-outlined" style={{ fontSize: '2.8rem' }}>person</span>
        </div>
        <div>
          <h2 className="fw-bolder m-0" style={{ color: 'var(--text-main)', letterSpacing: '-0.5px' }}>Profilim</h2>
          <p className="m-0" style={{ color: 'var(--text-muted)' }}>
            {profil?.kullanici_adi} • <span className="text-capitalize">{(profil?.rol || '').replace('_', ' ')}</span>
            {profil?.is_demo && <Badge bg="warning" text="dark" className="ms-2">Demo</Badge>}
          </p>
        </div>
      </div>

      <Row className="g-4">
        {/* SOL KOLON - Profil Bilgileri + Şifre */}
        <Col lg={6}>
          {/* Profil Bilgileri Kartı */}
          <Card className="glass-card mb-4" style={{ border: 'none' }}>
            <Card.Body className="p-4">
              <div className="d-flex align-items-center justify-content-between mb-4">
                <div className="d-flex align-items-center gap-2">
                  <span className="material-symbols-outlined" style={{ color: 'var(--accent-primary)' }}>badge</span>
                  <h5 className="m-0 fw-bold" style={{ color: 'var(--text-main)' }}>Hesap Bilgileri</h5>
                </div>
                {!editMode && (
                  <Button variant="link" className="p-0" onClick={() => setEditMode(true)} style={{ color: 'var(--accent-primary)' }}>
                    <span className="material-symbols-outlined" style={{ fontSize: '1.2rem' }}>edit</span>
                  </Button>
                )}
              </div>

              {profilMsg.text && <Alert variant={profilMsg.type} dismissible onClose={() => setProfilMsg({ text: '', type: '' })}>{profilMsg.text}</Alert>}

              {editMode ? (
                <Form onSubmit={handleProfilUpdate}>
                  <Form.Group className="mb-3">
                    <Form.Label style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>E-posta</Form.Label>
                    <Form.Control type="email" value={editData.eposta} onChange={e => setEditData({ ...editData, eposta: e.target.value })} style={inputStyle} />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>Telefon</Form.Label>
                    <Form.Control type="text" value={editData.telefon} onChange={e => setEditData({ ...editData, telefon: e.target.value })} style={inputStyle} />
                  </Form.Group>
                  <div className="d-flex gap-2">
                    <Button type="submit" size="sm" style={{ backgroundColor: 'var(--accent-primary)', border: 'none', color: '#fff' }}>Kaydet</Button>
                    <Button variant="secondary" size="sm" onClick={() => { setEditMode(false); setEditData({ eposta: profil?.eposta || '', telefon: profil?.telefon || '' }); }}>İptal</Button>
                  </div>
                </Form>
              ) : (
                <div className="d-flex flex-column gap-3">
                  {[
                    { icon: 'person', label: 'Kullanıcı Adı', value: profil?.kullanici_adi },
                    { icon: 'mail', label: 'E-posta', value: profil?.eposta },
                    { icon: 'call', label: 'Telefon', value: profil?.telefon || '—' },
                    { icon: 'apartment', label: 'Klinik', value: profil?.klinik_adi || '—' },
                    { icon: 'calendar_today', label: 'Kayıt Tarihi', value: profil?.kayit_tarihi ? new Date(profil.kayit_tarihi).toLocaleDateString('tr-TR') : '—' },
                    { icon: 'event_available', label: 'Abonelik Bitiş', value: profil?.abonelik_bitis_tarihi ? new Date(profil.abonelik_bitis_tarihi).toLocaleDateString('tr-TR') : '—' },
                  ].map((item, i) => (
                    <div key={i} className="d-flex align-items-center gap-3">
                      <span className="material-symbols-outlined" style={{ color: 'var(--text-muted)', fontSize: '1.1rem' }}>{item.icon}</span>
                      <div>
                        <div style={{ color: 'var(--text-muted)', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{item.label}</div>
                        <div style={{ color: 'var(--text-main)', fontWeight: '600' }}>{item.value}</div>
                      </div>
                    </div>
                  ))}
                  {profil?.paket_adlari?.length > 0 && (
                    <div className="d-flex align-items-center gap-3">
                      <span className="material-symbols-outlined" style={{ color: 'var(--text-muted)', fontSize: '1.1rem' }}>inventory_2</span>
                      <div>
                        <div style={{ color: 'var(--text-muted)', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Paketler</div>
                        <div className="d-flex gap-1 flex-wrap">{profil.paket_adlari.map((p, i) => <Badge key={i} bg="primary" className="fw-normal">{p}</Badge>)}</div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </Card.Body>
          </Card>

          {/* Şifre Değiştirme Kartı */}
          <Card className="glass-card" style={{ border: 'none' }}>
            <Card.Body className="p-4">
              <div className="d-flex align-items-center gap-2 mb-4">
                <span className="material-symbols-outlined" style={{ color: 'var(--accent-tertiary)' }}>lock</span>
                <h5 className="m-0 fw-bold" style={{ color: 'var(--text-main)' }}>Şifre Değiştir</h5>
              </div>

              {sifreMsg.text && <Alert variant={sifreMsg.type} dismissible onClose={() => setSifreMsg({ text: '', type: '' })}>{sifreMsg.text}</Alert>}

              <Form onSubmit={handleSifreDegistir}>
                <Form.Group className="mb-3">
                  <Form.Label style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>Mevcut Şifre</Form.Label>
                  <Form.Control type="password" value={mevcutSifre} onChange={e => setMevcutSifre(e.target.value)} required placeholder="Mevcut şifreniz" style={inputStyle} />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>Yeni Şifre</Form.Label>
                  <Form.Control type="password" value={yeniSifre} onChange={e => setYeniSifre(e.target.value)} required placeholder="En az 8 karakter" style={inputStyle} />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>Yeni Şifre (Tekrar)</Form.Label>
                  <Form.Control type="password" value={yeniSifreTekrar} onChange={e => setYeniSifreTekrar(e.target.value)} required placeholder="Şifreyi tekrar girin" style={inputStyle} />
                </Form.Group>
                <Button type="submit" disabled={sifreLoading} className="w-100 fw-bold" style={{ background: 'linear-gradient(135deg, var(--accent-primary), var(--accent-tertiary))', border: 'none' }}>
                  {sifreLoading ? <Spinner as="span" animation="border" size="sm" /> : 'Şifreyi Değiştir'}
                </Button>
              </Form>
            </Card.Body>
          </Card>

          {/* Logo Kartı */}
          <Card className="glass-card mt-4" style={{ border: 'none' }}>
            <Card.Body className="p-4">
              <div className="d-flex align-items-center gap-2 mb-3">
                <span className="material-symbols-outlined" style={{ color: 'var(--accent-primary)' }}>branding_watermark</span>
                <h5 className="m-0 fw-bold" style={{ color: 'var(--text-main)' }}>Logom</h5>
              </div>
              <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                Logonuz panel başlığında ve danışanlarınıza verdiğiniz beslenme planı çıktılarında görünür.
              </p>

              {logoMsg.text && <Alert variant={logoMsg.type} dismissible onClose={() => setLogoMsg({ text: '', type: '' })}>{logoMsg.text}</Alert>}

              <div className="d-flex align-items-center gap-3">
                <div className="d-flex align-items-center justify-content-center rounded-3 flex-shrink-0"
                  style={{ width: '110px', height: '70px', backgroundColor: '#ffffff', border: '1px solid var(--border-color)', overflow: 'hidden' }}>
                  {profil?.logo_data ? (
                    <img src={profil.logo_data} alt="Logo" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                  ) : (
                    <span className="material-symbols-outlined" style={{ color: '#9ca3af', fontSize: '2rem' }}>image</span>
                  )}
                </div>
                <div className="d-flex flex-column gap-2">
                  <input type="file" id="logo-input" accept="image/png,image/jpeg,image/webp" style={{ display: 'none' }} onChange={handleLogoSec} />
                  <Button size="sm" disabled={logoLoading} onClick={() => document.getElementById('logo-input').click()}
                    style={{ backgroundColor: 'var(--accent-primary)', border: 'none' }}>
                    {logoLoading ? <Spinner size="sm" animation="border" /> : (profil?.logo_data ? 'Logoyu Değiştir' : 'Logo Yükle')}
                  </Button>
                  {profil?.logo_data && (
                    <Button variant="outline-danger" size="sm" onClick={handleLogoKaldir}>Kaldır</Button>
                  )}
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* SAĞ KOLON - Tema Seçici */}
        <Col lg={6}>
          <Card className="glass-card" style={{ border: 'none' }}>
            <Card.Body className="p-4">
              <div className="d-flex align-items-center gap-2 mb-4">
                <span className="material-symbols-outlined" style={{ color: 'var(--accent-primary)' }}>palette</span>
                <h5 className="m-0 fw-bold" style={{ color: 'var(--text-main)' }}>Tema Seçici</h5>
              </div>

              {/* Karanlık Temalar */}
              <h6 className="mb-3 d-flex align-items-center gap-2" style={{ color: 'var(--text-muted)', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '1px' }}>
                <span className="material-symbols-outlined" style={{ fontSize: '1rem' }}>dark_mode</span> Karanlık Temalar
              </h6>
              <div className="d-flex flex-column gap-2 mb-4">
                {DARK_THEMES.map(t => (
                  <div key={t.key}
                    onClick={() => onThemeChange(t.key)}
                    className="d-flex align-items-center gap-3 p-3 rounded-3"
                    style={{
                      cursor: 'pointer',
                      backgroundColor: themePreset === t.key ? 'var(--accent-primary)' : 'var(--bg-tertiary)',
                      color: themePreset === t.key ? '#fff' : 'var(--text-main)',
                      transition: 'all 0.2s ease',
                      border: themePreset === t.key ? '2px solid var(--accent-primary)' : '2px solid transparent',
                    }}
                  >
                    <div className="d-flex gap-1">
                      {t.colors.map((c, i) => <div key={i} style={{ width: '20px', height: '20px', borderRadius: '50%', backgroundColor: c, border: '2px solid rgba(255,255,255,0.2)' }} />)}
                    </div>
                    <span className="fw-bold" style={{ fontSize: '0.9rem' }}>{t.name}</span>
                    {themePreset === t.key && <span className="material-symbols-outlined ms-auto" style={{ fontSize: '1.2rem' }}>check_circle</span>}
                  </div>
                ))}
              </div>

              {/* Aydınlık Temalar */}
              <h6 className="mb-3 d-flex align-items-center gap-2" style={{ color: 'var(--text-muted)', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '1px' }}>
                <span className="material-symbols-outlined" style={{ fontSize: '1rem' }}>light_mode</span> Aydınlık Temalar
              </h6>
              <div className="d-flex flex-column gap-2">
                {LIGHT_THEMES.map(t => (
                  <div key={t.key}
                    onClick={() => onThemeChange(t.key)}
                    className="d-flex align-items-center gap-3 p-3 rounded-3"
                    style={{
                      cursor: 'pointer',
                      backgroundColor: themePreset === t.key ? t.colors[1] : 'var(--bg-tertiary)',
                      color: themePreset === t.key ? '#fff' : 'var(--text-main)',
                      transition: 'all 0.2s ease',
                      border: themePreset === t.key ? `2px solid ${t.colors[1]}` : '2px solid transparent',
                    }}
                  >
                    <div className="d-flex gap-1">
                      {t.colors.map((c, i) => <div key={i} style={{ width: '20px', height: '20px', borderRadius: '50%', backgroundColor: c, border: '2px solid rgba(0,0,0,0.1)' }} />)}
                    </div>
                    <span className="fw-bold" style={{ fontSize: '0.9rem' }}>{t.name}</span>
                    {themePreset === t.key && <span className="material-symbols-outlined ms-auto" style={{ fontSize: '1.2rem' }}>check_circle</span>}
                  </div>
                ))}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
}

export default Profil;
